"use client";

import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import BasicLoader from '../common/basic-loader';

interface TracePaginationControlsProps {
  /** "top" renders the previous-page control, "bottom" the next-page control. */
  position: "top" | "bottom";
  /** Current page index (0-based). */
  page: number;
  /** Number of traces shown per page. */
  pageSize: number;
  /** Real total (fact_count). Null for cursor-only pagination → "of Z" is omitted. */
  total: number | null;
  /** Traces currently loaded client-side (used to clamp ranges when total is unknown). */
  loadedCount: number;
  /** Whether a previous page is available. */
  hasPrev: boolean;
  /** Whether a next page is available (loaded or fetchable). */
  hasNext: boolean;
  /** True while the next page is being fetched from the server. */
  loadingMore: boolean;
  onPrev: () => void;
  onNext: () => void;
}

const buttonClasses =
  "inline-flex items-center gap-1.5 rounded border border-form-control-border-rest bg-canvas-background-secondary px-2 py-1.5 text-body-sm text-body-secondary transition-colors hover:border-[#34BCAF] hover:text-gray-100 disabled:cursor-not-allowed disabled:opacity-40";

// Build the "Show {start} - {end}[ of {total}]" label for a page index.
// When the real total is unknown (cursor-only), the "of Z" suffix is dropped
// and the end is clamped to what we know is loaded.
const buildLabel = (
  pageIndex: number,
  pageSize: number,
  total: number | null,
  loadedCount: number
): string => {
  const start = pageIndex * pageSize + 1;
  const nominalEnd = (pageIndex + 1) * pageSize;
  if (total != null) {
    return `Show ${start} - ${Math.min(nominalEnd, total)} of ${total}`;
  }
  // Unknown total (cursor-only): the end is exact for already-loaded pages,
  // clamped on the last partially-loaded page, and nominal for a not-yet-fetched
  // forward page. No "of Z" suffix since the total isn't known.
  let end: number;
  if (nominalEnd <= loadedCount) {
    end = nominalEnd; // page fully loaded
  } else if (start <= loadedCount) {
    end = loadedCount; // last, partially loaded page
  } else {
    end = nominalEnd; // not loaded yet — will fetch on click
  }
  return `Show ${start} - ${end}`;
};

// Renders just the prev/next button. Layout (indent, fixed row height) and the
// tree connector line are handled by the FactPaginationRow wrapper in tree-view.
const TracePaginationControls: React.FC<TracePaginationControlsProps> = ({
  position,
  page,
  pageSize,
  total,
  loadedCount,
  hasPrev,
  hasNext,
  loadingMore,
  onPrev,
  onNext,
}) => {
  if (position === "top") {
    if (!hasPrev) return null;
    return (
      <button
        type="button"
        className={buttonClasses}
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        disabled={loadingMore}
      >
        <ChevronLeft size={16} />
        <span>{buildLabel(page - 1, pageSize, total, loadedCount)}</span>
      </button>
    );
  }

  if (!hasNext) return null;
  return (
    <button
      type="button"
      className={buttonClasses}
      onClick={(e) => {
        e.stopPropagation();
        onNext();
      }}
      disabled={loadingMore}
    >
      {loadingMore ? (
        <BasicLoader className="size-4 inline align-middle">
          <span className="ml-1.5 align-middle">Loading…</span>
        </BasicLoader>
      ) : (
        <>
          <ChevronRight size={16} />
          <span>{buildLabel(page + 1, pageSize, total, loadedCount)}</span>
        </>
      )}
    </button>
  );
};

export default TracePaginationControls;
